import { useCallback, useEffect, useState } from 'react'
import { ServerApiClient, type ServerSite } from '../../data/ServerApiClient'
import { CfoBrandMark, navigateTo } from '../cfo-site/CfoSiteShared'
import { FgHelp } from '../help/FgHelp'
import { CapabilityBrowser } from './CapabilityBrowser'
import {
  APPSOURCE_URL, BOOK_WORKING_SESSION_URL, CHATGPT_DIRECTORY_URL, CLAUDE_CODE_COMMAND, CLAUDE_CONNECTORS_URL,
  EXCEL_ADMIN_DEPLOY_DOC, EXCEL_MANIFEST_PATH, MCP_URL, SITE_ADMIN_ROLES, connectApi, copyText, connectContext, type Connection, type Suggestion, type SuggestionList,
} from './connectApi'
import { GettingStartedChecklist } from './GettingStartedChecklist'
import './ConnectPage.css'

export type ConnectDeps = {
  api: typeof connectApi
  listSites: () => Promise<ServerSite[]>
}
const DEFAULT_DEPS: ConnectDeps = { api: connectApi, listSites: () => new ServerApiClient().listMySites() }

const SCOPE_LABELS: Record<string, string> = {
  'fusegrid:read': 'Read financials',
  'fusegrid:write': 'Make changes',
  'fusegrid:excel': 'Change Excel workbooks',
  // FE:C14 OpenID scopes (identity only, no FuseGrid data).
  openid: 'Confirm who you are',
  email: 'See your email address',
}
const scopeLabel = (scope: string) => SCOPE_LABELS[scope] ?? scope
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'never')

function CopyField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState<boolean | null>(null)
  return <div className="fg-copy-field">
    <code aria-label={label}>{value}</code>
    <button type="button" onClick={async () => setCopied(await copyText(value))}>{copied ? 'Copied' : 'Copy'}</button>
    {copied === false && <span role="alert">Copy failed; select the text instead.</span>}
  </div>
}

function ConnectionRow({ connection, onRevoke, onNarrow, showUser }: {
  connection: Connection
  onRevoke: () => Promise<void>
  onNarrow?: (scopes: string[]) => Promise<void>
  showUser?: boolean
}) {
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [kept, setKept] = useState(connection.scopes)
  const run = async (action: () => Promise<void>) => { setBusy(true); try { await action() } finally { setBusy(false) } }
  return <li className="fg-connection" data-client={connection.client_id}>
    <div className="fg-connection-head">
      <strong>{connection.client_name}</strong>
      {showUser && <span className="fg-connection-user">{connection.user_email}</span>}
      <span className="fg-connection-used">Last used {when(connection.last_used_at)}</span>
    </div>
    <div className="fg-connection-meta">
      <span>Companies: {connection.company_mode === 'all_accessible'
        ? 'All companies you can access'
        : connection.companies.map(c => c.name).join(', ') || 'none you can still access'}</span>
      <span>Can: {connection.scopes.map(scopeLabel).join(' · ')}</span>
    </div>
    {editing && onNarrow && <fieldset className="fg-connection-scopes">
      <legend>Keep these permissions<FgHelp id="connect.edit_scopes" /></legend>
      {connection.scopes.map(scope => <label key={scope}>
        <input type="checkbox" checked={kept.includes(scope)} disabled={scope === 'fusegrid:read'} onChange={event => setKept(event.target.checked ? [...kept, scope] : kept.filter(s => s !== scope))} />
        {scopeLabel(scope)}
      </label>)}
      <button type="button" disabled={busy || kept.length === 0} onClick={() => void run(async () => { await onNarrow(kept); setEditing(false) })}>Save</button>
      <button type="button" onClick={() => { setKept(connection.scopes); setEditing(false) }}>Cancel</button>
    </fieldset>}
    <div className="fg-connection-actions">
      {onNarrow && !editing && connection.scopes.length > 1 && <button type="button" onClick={() => setEditing(true)}>Edit permissions</button>}
      <button type="button" className="fg-danger" disabled={busy} onClick={() => void run(onRevoke)}>{busy ? 'Revoking…' : `Revoke ${connection.client_name}`}</button>
    </div>
  </li>
}

function MyConnections({ api }: { api: ConnectDeps['api'] }) {
  const [connections, setConnections] = useState<Connection[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => { api.myConnections().then(setConnections, () => setError('Your connections could not be loaded.')) }, [api])
  useEffect(load, [load])
  return <section className="fg-connect-card" aria-labelledby="fg-mine-title">
    <div className="fg-connect-card-head">
      <h2 id="fg-mine-title">Your connections<FgHelp id="connect.your_connections" /></h2>
      <p>AI apps you have connected to FuseGrid. Revoking takes effect on the app's next request.</p>
    </div>
    {error && <p className="fg-connect-error" role="alert">{error}</p>}
    {connections?.length === 0 && <p className="fg-connect-muted">No AI apps are connected yet. Use the Claude or ChatGPT steps above.</p>}
    {connections && connections.length > 0 && <ul className="fg-connections">
      {connections.map(connection => <ConnectionRow key={connection.grant_id} connection={connection}
        onNarrow={async scopes => { await api.narrowScopes(connection, scopes); load() }}
        onRevoke={async () => { await api.revokeMine(connection); load() }} />)}
    </ul>}
  </section>
}

function FirmConnections({ api, sites }: { api: ConnectDeps['api']; sites: ServerSite[] }) {
  const [siteId, setSiteId] = useState(sites[0]?.id ?? '')
  const [connections, setConnections] = useState<Connection[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => {
    if (!siteId) return
    setError(null)
    api.firmConnections(siteId).then(setConnections, () => setError('Firm connections could not be loaded.'))
  }, [api, siteId])
  useEffect(load, [load])
  return <section className="fg-connect-card" aria-labelledby="fg-firm-title">
    <div className="fg-connect-card-head">
      <h2 id="fg-firm-title">Your firm's connections<FgHelp id="connect.firm_connections" /></h2>
      <p>Every AI app your firm's members have connected to this firm's companies. You can revoke any of them.</p>
    </div>
    {sites.length > 1 && <label className="fg-connect-inline">Firm
      <select value={siteId} onChange={event => setSiteId(event.target.value)}>
        {sites.map(site => <option key={site.id} value={site.id}>{site.name}</option>)}
      </select>
    </label>}
    {error && <p className="fg-connect-error" role="alert">{error}</p>}
    {connections?.length === 0 && <p className="fg-connect-muted">No member of this firm has connected an AI app.</p>}
    {connections && connections.length > 0 && <ul className="fg-connections">
      {connections.map(connection => <ConnectionRow key={connection.grant_id} connection={connection} showUser
        onRevoke={async () => { await api.revokeForMember(siteId, connection); load() }} />)}
    </ul>}
  </section>
}

function CoachMode({ api }: { api: ConnectDeps['api'] }) {
  const [on, setOn] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { api.coachMode().then(setOn, () => setError('Could not load your assistant preference.')) }, [api])
  return <section className="fg-connect-card fg-coach" aria-labelledby="fg-coach-title">
    <div className="fg-connect-card-head">
      <h2 id="fg-coach-title">Coach mode<FgHelp id="connect.coach_mode" /></h2>
      <p>The assistant explains its plan first, previews every change, and tells you how to check the result.</p>
    </div>
    {error && <p className="fg-connect-error" role="alert">{error}</p>}
    {on !== null && <label className="fg-connect-inline">
      <input type="checkbox" checked={on} onChange={async event => {
        const next = event.target.checked
        try { setOn(await api.setCoachMode(next)) } catch { setError('Could not save your assistant preference.') }
      }} />
      Coach me as I work
    </label>}
  </section>
}

type SuggestionCompany = NonNullable<SuggestionList['company']>

/** FE:C11 — "Try next": read-only requests for the company the person came from; hidden when none load. */
function TryNext({ api, company, onCompany }: { api: ConnectDeps['api']; company: string | null; onCompany: (company: SuggestionCompany | null) => void }) {
  const [items, setItems] = useState<Suggestion[]>([])
  const [copied, setCopied] = useState<string | null>(null)
  useEffect(() => {
    api.suggestions('connect', 4, company ?? undefined).then(
      list => { setItems(list.suggestions); onCompany(list.company ?? null) },
      () => { setItems([]); onCompany(null) },
    )
  }, [api, company, onCompany])
  if (!items.length) return null
  return <p className="fg-connect-next" aria-label="Try next">
    Try next:{' '}
    {items.map(item => item.kind === 'playbook'
      ? <a key={`p:${item.id}`} className="fg-connect-chip" href={`/playbooks#${encodeURIComponent(item.id)}`}>{item.label}</a>
      : <button key={`e:${item.id}`} type="button" className="fg-connect-chip" onClick={async () => { if (await copyText(item.label)) setCopied(item.id) }}>
        {copied === item.id ? 'Copied' : item.label}
      </button>)}
  </p>
}

export function ConnectPage({ deps = DEFAULT_DEPS }: { deps?: ConnectDeps }) {
  const [context] = useState(() => connectContext(window.location.search))
  const [company, setCompany] = useState<SuggestionCompany | null>(null)
  const [adminSites, setAdminSites] = useState<ServerSite[]>([])
  useEffect(() => {
    deps.listSites().then(sites => setAdminSites(sites.filter(site => SITE_ADMIN_ROLES.has(String(site.current_site_role || '')))), () => setAdminSites([]))
  }, [deps])

  return <div className="fg-connect">
    <nav className="fg-connect-nav">
      <a href="/" onClick={event => { event.preventDefault(); navigateTo('/') }}><CfoBrandMark sub="Connect" /></a>
      <span>
        <a href="/playbooks">Playbooks</a>
        <a href="/ai-activity">What the AI did</a>
        <a href="/support">Support</a>
      </span>
    </nav>
    <main className="fg-connect-main">
      <header className="fg-connect-hero">
        {company && <a className="fg-connect-back" href={context.from}>Back to {company.display_name}</a>}
        <h1>{company ? `Connecting AI for ${company.display_name}` : 'Connect FuseGrid'}<FgHelp id="connect.page" /></h1>
        <p>Use your clients' governed numbers in Excel, Claude and ChatGPT. Every app reaches the same companies you can, with the same permissions.</p>
        <a className="fg-connect-button" href={BOOK_WORKING_SESSION_URL}>Book a working session</a>
        <TryNext api={deps.api} company={context.company} onCompany={setCompany} />
      </header>

      <GettingStartedChecklist api={deps.api} />

      <div className="fg-connect-grid">
        <section className="fg-connect-card" id="connect-excel" aria-labelledby="fg-excel-title">
          <div className="fg-connect-card-head">
            <h2 id="fg-excel-title">Excel<FgHelp id="connect.excel" /></h2>
            <p>FuseGrid for Excel puts refreshable statements, cash and reports in your workbooks, with an assistant in the sidebar.</p>
          </div>
          <ol>
            <li><a href={APPSOURCE_URL} target="_blank" rel="noreferrer">Get FuseGrid for Excel from Microsoft AppSource</a></li>
            <li>Or <a href={EXCEL_MANIFEST_PATH} download>download the add-in manifest</a> to sideload it.</li>
            <li>IT admins can deploy it to the whole firm: <a href={EXCEL_ADMIN_DEPLOY_DOC} target="_blank" rel="noreferrer">Microsoft 365 admin deployment guide</a>.</li>
          </ol>
          <p>Or download the whole CFO workbook from any company page.</p>
        </section>

        <section className="fg-connect-card" id="connect-claude" aria-labelledby="fg-claude-title">
          <div className="fg-connect-card-head">
            <h2 id="fg-claude-title">Claude<FgHelp id="connect.claude" /></h2>
            <p>Add FuseGrid as a custom connector, then sign in and choose which companies Claude may use.</p>
          </div>
          <ol>
            <li>Copy the FuseGrid connector URL:<CopyField value={MCP_URL} label="FuseGrid connector URL" /></li>
            <li>Open <a href={CLAUDE_CONNECTORS_URL} target="_blank" rel="noreferrer">Claude Settings → Connectors</a>, choose <strong>Add custom connector</strong>, paste the URL and choose <strong>Connect</strong>.</li>
            <li>Using Claude Code? Run:<CopyField value={CLAUDE_CODE_COMMAND} label="Claude Code command" /></li>
          </ol>
        </section>

        <section className="fg-connect-card" id="connect-chatgpt" aria-labelledby="fg-chatgpt-title">
          <div className="fg-connect-card-head">
            <h2 id="fg-chatgpt-title">ChatGPT<FgHelp id="connect.chatgpt" /></h2>
            <p>Until FuseGrid is listed in the <a href={CHATGPT_DIRECTORY_URL} target="_blank" rel="noreferrer">ChatGPT app directory</a>, add it in developer mode.</p>
          </div>
          <ol>
            <li>In ChatGPT open <strong>Settings → Apps &amp; Connectors → Advanced settings</strong> and turn on <strong>Developer mode</strong>.</li>
            <li>Choose <strong>Create</strong>, name it FuseGrid, and paste the connector URL:<CopyField value={MCP_URL} label="FuseGrid connector URL for ChatGPT" /></li>
            <li>Set authentication to <strong>OAuth</strong>, choose <strong>Create</strong>, then sign in to FuseGrid and approve.</li>
          </ol>
        </section>
      </div>

      <MyConnections api={deps.api} />
      {adminSites.length > 0 && <FirmConnections api={deps.api} sites={adminSites} />}
      <CoachMode api={deps.api} />
      <CapabilityBrowser />

      <section className="fg-connect-card fg-connect-human" aria-labelledby="fg-human-title">
        <h2 id="fg-human-title">Prefer a person?</h2>
        <p>Book a working session and we will set up your first client with you, or <a href="/support">contact support</a>.</p>
        <a className="fg-connect-button" href={BOOK_WORKING_SESSION_URL}>Book a working session</a>
      </section>
    </main>
  </div>
}

export default ConnectPage
